import { useState } from 'react';
import axios from 'axios';
import { THRESHOLD_METRICS, getMetricByKey, VitalIcon } from './vitalsConfig';
import Modal, { ModalCloseButton } from './ui/Modal';

// Severity & operator are intentionally fixed.
// The simplified model only supports a single "Normal Range" definition
// per metric; any reading outside [valueLow, valueHigh] is considered
// abnormal and raises an alert.
const FIXED_SEVERITY = 'Normal';
const FIXED_OPERATOR = 'between';

const emptyForm = () => ({
    metric: '',
    valueLow: '',
    valueHigh: '',
    changeReason: ''
});

const ThresholdsTab = ({ allThresholds, setAllThresholds, pmrn, clinicianId, API_BASE_URL }) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [editingThreshold, setEditingThreshold] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(emptyForm());

    const formatDateTime = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return (
            date.toLocaleDateString('en-US') +
            ' ' +
            date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            })
        );
    };

    const metricLabelText = (metricValue) => {
        const m = getMetricByKey(metricValue);
        return m ? `${m.label} (${m.unit})` : metricValue;
    };

    const metricLabel = (metricValue) => {
        const m = getMetricByKey(metricValue);
        if (!m) return metricValue;
        return (
            <span className="inline-flex items-center gap-1.5">
                {m.icon ? <VitalIcon icon={m.icon} className="w-3.5 h-3.5 text-teal-700" /> : null}
                <span>{m.label} ({m.unit})</span>
            </span>
        );
    };

    const safeParseFloat = (value) => {
        if (value === null || value === undefined) return null;
        const str = value.toString().trim();
        if (str === '') return null;
        const num = parseFloat(str);
        return isNaN(num) ? null : num;
    };

    // A configured range is valid when:
    // - A metric is selected
    // - At least one of (low, high) is provided
    // - If both are provided, low must be <= high
    const isFormValid = () => {
        if (!formData.metric) return false;
        const low = safeParseFloat(formData.valueLow);
        const high = safeParseFloat(formData.valueHigh);
        if (low === null && high === null) return false;
        if (low !== null && high !== null && low > high) return false;
        return true;
    };

    // Metrics already configured for this patient so we can prevent duplicates
    // when adding a new one (edit still allows changing bounds).
    const configuredMetricKeys = new Set(
        (allThresholds || []).map((t) => t.metric).filter(Boolean)
    );

    const availableMetricsForAdd = THRESHOLD_METRICS.filter(
        (m) => !configuredMetricKeys.has(m.value)
    );

    const handleMetricChange = (metricValue) => {
        const m = getMetricByKey(metricValue);
        const defMin = m?.defaultRange?.min ?? '';
        const defMax = m?.defaultRange?.max ?? '';
        setFormData({
            ...formData,
            metric: metricValue,
            valueLow: defMin === null || defMin === undefined ? '' : defMin.toString(),
            valueHigh: defMax === null || defMax === undefined ? '' : defMax.toString()
        });
    };

    const buildPayload = () => ({
        PMRN: pmrn,
        clinicianId: clinicianId,
        metric: formData.metric,
        operator: FIXED_OPERATOR,
        severity: FIXED_SEVERITY,
        changeReason: formData.changeReason || null,
        valueLow: safeParseFloat(formData.valueLow),
        valueHigh: safeParseFloat(formData.valueHigh)
    });

    const handleAddThreshold = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${API_BASE_URL}/thresholds/`, buildPayload());
            setAllThresholds((prev) => [...prev, response.data]);
            setShowAddModal(false);
            setFormData(emptyForm());
        } catch (error) {
            console.error('Error adding threshold:', error);
            alert('Failed to add threshold: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleEditThreshold = async () => {
        try {
            setLoading(true);
            const response = await axios.put(
                `${API_BASE_URL}/thresholds/${editingThreshold.id}`,
                buildPayload()
            );
            setAllThresholds((prev) =>
                prev.map((t) => (t.id === editingThreshold.id ? response.data : t))
            );
            setShowEditModal(false);
            setEditingThreshold(null);
            setFormData(emptyForm());
        } catch (error) {
            console.error('Error updating threshold:', error);
            alert('Failed to update threshold: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteThreshold = async (thresholdId) => {
        if (!window.confirm('Are you sure you want to delete this threshold?')) return;
        try {
            setLoading(true);
            await axios.delete(`${API_BASE_URL}/thresholds/${thresholdId}`);
            setAllThresholds((prev) => prev.filter((t) => t.id !== thresholdId));
        } catch (error) {
            console.error('Error deleting threshold:', error);
            alert('Failed to delete threshold: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleViewHistory = async (threshold) => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_BASE_URL}/thresholds/${threshold.id}/history`
            );
            setHistoryData(response.data);
            setEditingThreshold(threshold);
            setShowHistoryModal(true);
        } catch (error) {
            console.error('Error fetching history:', error);
            alert('Failed to load history: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setFormData(emptyForm());
        setShowAddModal(true);
    };

    const openEditModal = (threshold) => {
        setEditingThreshold(threshold);
        setFormData({
            metric: threshold.metric,
            valueLow: threshold.valueLow != null ? threshold.valueLow.toString() : '',
            valueHigh: threshold.valueHigh != null ? threshold.valueHigh.toString() : '',
            changeReason: ''
        });
        setShowEditModal(true);
    };

    const renderRangeFields = () => {
        const selected = getMetricByKey(formData.metric);
        const unit = selected ? selected.unit : '';
        return (
            <>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Normal Range – Low {unit && `(${unit})`}
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        placeholder="e.g. 65"
                        value={formData.valueLow}
                        onChange={(e) =>
                            setFormData({ ...formData, valueLow: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Normal Range – High {unit && `(${unit})`}
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        placeholder="e.g. 95"
                        value={formData.valueHigh}
                        onChange={(e) =>
                            setFormData({ ...formData, valueHigh: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500"
                    />
                </div>
                <p className="text-xs text-gray-500">
                    An alert is raised for any reading strictly below the low value or strictly above the high value.
                    Leave a bound blank to make it one-sided (e.g. only a minimum for Steps).
                </p>
            </>
        );
    };

    return (
        <div className="space-y-6">
            {/* Current Thresholds Table */}
            <div className="bg-white p-6 rounded-md shadow-sm border">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-700">Normal Ranges</h3>
                        <p className="text-xs text-gray-500 mt-1">
                            Any reading outside a configured normal range automatically triggers an alert.
                        </p>
                    </div>
                    <button
                        onClick={openAddModal}
                        disabled={availableMetricsForAdd.length === 0}
                        className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Add Normal Range
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Metric</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Low</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">High</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Created</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {allThresholds && allThresholds.length > 0 ? (
                                allThresholds.map((threshold) => (
                                    <tr key={threshold.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 font-medium">
                                            {metricLabel(threshold.metric)}
                                        </td>
                                        <td className="px-4 py-2">{threshold.valueLow ?? '—'}</td>
                                        <td className="px-4 py-2">{threshold.valueHigh ?? '—'}</td>
                                        <td className="px-4 py-2">
                                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                Normal range set
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            {formatDateTime(threshold.createdAt)}
                                        </td>
                                        <td className="px-4 py-2 space-x-2">
                                            <button
                                                onClick={() => openEditModal(threshold)}
                                                className="text-blue-600 hover:text-blue-800 text-sm"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleViewHistory(threshold)}
                                                className="text-green-600 hover:text-green-800 text-sm"
                                            >
                                                History
                                            </button>
                                            <button
                                                onClick={() => handleDeleteThreshold(threshold.id)}
                                                className="text-red-600 hover:text-red-800 text-sm"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-gray-500">
                                        No normal ranges configured yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Threshold Modal */}
            <Modal
                open={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setFormData(emptyForm());
                }}
            >
                    <div className="bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 w-96">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Add Normal Range</h3>
                            <ModalCloseButton
                                onClose={() => {
                                    setShowAddModal(false);
                                    setFormData(emptyForm());
                                }}
                            />
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Vital Sign / Metric
                                </label>
                                <select
                                    value={formData.metric}
                                    onChange={(e) => handleMetricChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500"
                                    required
                                >
                                    <option value="">-- Select Metric --</option>
                                    {availableMetricsForAdd.map((m) => (
                                        <option key={m.value} value={m.value}>
                                            {m.label} ({m.unit})
                                        </option>
                                    ))}
                                </select>
                                {availableMetricsForAdd.length === 0 && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        All available metrics already have a configured range.
                                    </p>
                                )}
                            </div>

                            {formData.metric && renderRangeFields()}

                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={handleAddThreshold}
                                    disabled={!isFormValid() || loading}
                                    className="flex-1 bg-teal-600 text-white py-2 rounded hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Adding...' : 'Save Range'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setFormData(emptyForm());
                                    }}
                                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
            </Modal>

            {/* Edit Threshold Modal */}
            <Modal
                open={showEditModal && !!editingThreshold}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingThreshold(null);
                    setFormData(emptyForm());
                }}
            >
                    <div className="bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 w-96">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Edit Normal Range</h3>
                            <ModalCloseButton
                                onClose={() => {
                                    setShowEditModal(false);
                                    setEditingThreshold(null);
                                    setFormData(emptyForm());
                                }}
                            />
                        </div>
                        <div className="space-y-4">
                            <input
                                type="text"
                                value={metricLabelText(formData.metric)}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100"
                            />

                            {renderRangeFields()}

                            <input
                                type="text"
                                placeholder="Reason for change (optional)"
                                value={formData.changeReason}
                                onChange={(e) =>
                                    setFormData({ ...formData, changeReason: e.target.value })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                            />

                            <div className="flex gap-2">
                                <button
                                    onClick={handleEditThreshold}
                                    disabled={!isFormValid() || loading}
                                    className="flex-1 bg-teal-600 text-white py-2 rounded hover:bg-teal-500 disabled:opacity-50"
                                >
                                    {loading ? 'Updating...' : 'Update'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingThreshold(null);
                                        setFormData(emptyForm());
                                    }}
                                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
            </Modal>

            {/* History Modal */}
            <Modal
                open={showHistoryModal && !!editingThreshold}
                onClose={() => {
                    setShowHistoryModal(false);
                    setEditingThreshold(null);
                }}
            >
                    <div className="bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 w-full max-w-3xl max-h-96 overflow-y-auto">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Change History - {metricLabelText(editingThreshold?.metric)}
                            </h3>
                            <ModalCloseButton
                                onClose={() => {
                                    setShowHistoryModal(false);
                                    setEditingThreshold(null);
                                }}
                            />
                        </div>
                        {historyData.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Date</th>
                                        <th className="px-4 py-2 text-left">Old Range</th>
                                        <th className="px-4 py-2 text-left">New Range</th>
                                        <th className="px-4 py-2 text-left">Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {historyData.map((entry) => (
                                        <tr key={entry.id}>
                                            <td className="px-4 py-2 text-xs">
                                                {formatDateTime(entry.changedAt)}
                                            </td>
                                            <td className="px-4 py-2 text-xs">
                                                {entry.oldValueLow ?? '—'} – {entry.oldValueHigh ?? '—'}
                                            </td>
                                            <td className="px-4 py-2 text-xs">
                                                {entry.newValueLow ?? '—'} – {entry.newValueHigh ?? '—'}
                                            </td>
                                            <td className="px-4 py-2 text-xs">
                                                {entry.changeReason || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-gray-500 text-center py-4">
                                No change history available.
                            </p>
                        )}
                        <button
                            onClick={() => {
                                setShowHistoryModal(false);
                                setEditingThreshold(null);
                            }}
                            className="w-full mt-4 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400"
                        >
                            Close
                        </button>
                    </div>
            </Modal>
        </div>
    );
};

export default ThresholdsTab;
